import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { StateService } from '@uirouter/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { SessionService } from 'account-hybrid/core/authentication';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import * as ContestsActions from '../../../../account-v2/src/app/features/advocacy/group/contests/actions';
import PropertyApi from '../../../../account-v2/src/app/actions/propertyApi';
import { AdvocacyNgReduxFacade } from '../store/advocacy-ngredux.facade';
import { CreateContestModalService } from '../create-contest-modal/create-contest-modal.service';

/**
 * Native-Angular port of the AngularJS `ftContests` controller
 * (account-v2/.../advocacy/group/contests/contests.component.ts).
 *
 * Drives the /advocacy/contests leaf. Follows the bigPicture recipe:
 * action thunks imported as ES modules, advocacy state through the native
 * NgRx facade. The create-contest modal is the native
 * CreateContestModalService (inc 2y) — the last `$injector` grab is gone.
 *
 * For the QA-login parity gate the visible content is just the
 * Current/Upcoming/Past sub-tab nav + the "no campaigns" placeholder
 * (the property has no contests). The contest-card markup is kept
 * verbatim for future visual diffs once a property with contests is
 * recorded; it's hidden behind the photoAdvocacy.contests list being
 * empty for now.
 */
@Component({
    selector: 'ft-contests',
    templateUrl: './contests.component.html',
    standalone: false
})
export class ContestsComponent implements OnInit, OnDestroy {
  property: any;
  photoAdvocacy: any = {};
  canEditData = false;
  isInitialized = false;
  sectionsRegistry = [
    { name: 'current', title: 'Current', isActive: true, isDisabled: false },
    { name: 'upcoming', title: 'Upcoming', isActive: false, isDisabled: false },
    { name: 'past', title: 'Past', isActive: false, isDisabled: false },
  ];

  private unsubscribe?: () => void;
  private $ngRedux: any;
  // WS5 #10 inc 2y: the native create-contest modal launcher via a FIELD
  // INITIALIZER (inject() in ngOnInit throws NG0203); the v1
  // 'ftCreateContestModal' grab is gone.
  private readonly createContestModal = inject(CreateContestModalService);
  private readonly parse = inject(ParseService);
  private readonly eventBus = inject(EventBusService);
  private offBus: Array<() => void> = [];

  constructor(
    private readonly spacesService: SpacesService,
    private readonly sessionService: SessionService,
    private readonly stateService: StateService,
    @Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment,
    private readonly advStore: AdvocacyNgReduxFacade,
  ) {}

  ngOnInit(): void {
    this.property = this.sessionService.getProperty(this.spacesService.getPropertyUuid()!);
    // (Permissions.AdvocacyWrite is a bit-flag set on session.Permissions —
    // the legacy controller did `(property.Permissions & X) === X`.)
    // Value 2 mirrors Permissions.AdvocacyWrite (see account-hybrid/core/authentication).
    const ADVOCACY_WRITE = 2;
    this.canEditData = (this.property?.Permissions & ADVOCACY_WRITE) === ADVOCACY_WRITE;

    // WS2 strangle: advocacy state now in the native NgRx feature (facade).
    this.$ngRedux = this.advStore;

    this.unsubscribe = this.$ngRedux.subscribe(() => this.syncFromState());
    this.syncFromState();
    this.load();

    // The contest editors open over this list as child states; refetch when one changes a contest.
    this.offBus = ['flipto.photo-contest-activated', 'flipto.photo-contest-closed', 'flipto.photo-contest-changed'].map((name) =>
      this.eventBus.on(name, () => this.fetchContests()),
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe?.();
    this.offBus.forEach((off) => off());
  }

  private syncFromState(): void {
    const state = this.$ngRedux.getState();
    this.photoAdvocacy = state?.apiData?.propertyPlatformData?.photoAdvocacy ?? {};
    // state.views.contests exposes per-view UI flags; spread them onto `this`
    // the same way the legacy `$ngRedux.connect` mapping did.
    Object.assign(this, state?.views?.contests ?? {});
  }

  load(): void {
    this.$ngRedux.dispatch(ContestsActions.initialized(false));
    this.fetchContests().then(() => {
      this.isInitialized = true;
      this.$ngRedux.dispatch(ContestsActions.initialized(true));
    });
  }

  /** Unlike `load`, this leaves `isInitialized` alone, so a refetch is not a reload. */
  private fetchContests(): Promise<unknown> {
    return Promise.all([
      this.$ngRedux.dispatch(PropertyApi.getPhotoAdvocacyLayerData(this.property.UUID)),
      this.$ngRedux.dispatch(PropertyApi.getPhotoContestLayerData(this.property.UUID)),
    ]);
  }

  getContestBackgroundImage(contest: any): string {
    if (contest?.activityPhoto) {
      return buildAzureStoredPhotoUrl(contest.activityPhoto, undefined, this.environment.AzureCdnBaseUrl) as string;
    }
    return contest?.propertyBackgroundImageUrl ?? '';
  }

  showContest(contest: any, activeSection: string): void {
    this.stateService.go(`advocacy.contests.${activeSection}`, { contestUuid: contest.id });
  }

  createNewContest(): void {
    // v1 (account-v2 contests.component.ts:101) wrapped with
    // Parse.arrayOrDefault — clonableContests is the {item:…} JSON convention,
    // NOT a plain array. The earlier consumer port's `?? []` passed the raw
    // wrapper and the modal's $onInit crashed on .slice() (pre-existing,
    // load-gate-blind; caught by the 2y route-abort diag).
    this.createContestModal.show(
      this.property.UUID,
      this.parse.arrayOrDefault(this.photoAdvocacy?.clonableContests),
      (contestUuid: string) => {
        this.stateService.go('advocacy.contests.details', { contestUuid });
        this.load();
      },
      () => alert('Could not create contest.'),
    );
  }
}
